import { isUndefined } from 'lodash';
import * as THREE from 'three';
import { dataComponents } from './constants';
import type {
  CrossSectionPointData,
  PointBatchRendererOptions,
  PointData,
  RendererStats,
} from './types';

// Constants.
const DEFAULT_BATCH_SIZE = 150000;

// PointBatch Class.

/**
 * Represents a single batch of points with pre-allocated buffers.
 */
export class PointBatch {
  public readonly geometry: THREE.BufferGeometry;
  public readonly material: THREE.PointsMaterial;
  public readonly points: THREE.Points;
  public count: number = 0;

  private readonly maxCapacity: number;
  private readonly positionAttribute: THREE.BufferAttribute;
  private readonly colorAttribute: THREE.BufferAttribute;

  constructor(batchSize: number, material: THREE.PointsMaterial) {
    this.maxCapacity = batchSize;
    this.material = material;
    this.geometry = new THREE.BufferGeometry();

    // Pre-allocate typed arrays for performance.
    const { targetPositionComponents, colorComponents } = dataComponents;
    const positions = new Float32Array(batchSize * targetPositionComponents);
    const colors = new Uint8Array(batchSize * colorComponents);

    this.positionAttribute = new THREE.BufferAttribute(
      positions,
      targetPositionComponents,
    );
    this.colorAttribute = new THREE.BufferAttribute(
      colors,
      colorComponents,
      true,
    );

    this.geometry.setAttribute('position', this.positionAttribute);
    this.geometry.setAttribute('color', this.colorAttribute);
    this.geometry.setDrawRange(0, 0);

    this.points = new THREE.Points(this.geometry, this.material);
  }

  /**
   * Update geometry attributes after adding points.
   */
  private updateGeometry(): void {
    this.geometry.setDrawRange(0, this.count);
    this.positionAttribute.needsUpdate = true;
    this.colorAttribute.needsUpdate = true;
    this.geometry.computeBoundingSphere();
  }

  /**
   * Check if batch is at full capacity.
   */
  isFull(): boolean {
    return this.count >= this.maxCapacity;
  }

  /**
   * Add points directly from TypedArrays without object conversion.
   * @param data Source TypedArray data
   * @param sourceOffset Starting index in source data
   * @param count Number of points to add
   * @returns Number of points actually added
   */
  addPoints(
    data: CrossSectionPointData,
    sourceOffset: number,
    count: number,
  ): number {
    const available = this.maxCapacity - this.count;
    const toAdd = Math.min(count, available);

    if (toAdd <= 0) {
      return 0;
    }

    const targetPositions = this.positionAttribute.array as Float32Array;
    const targetColors = this.colorAttribute.array as Uint8Array;

    const {
      sourcePositionComponents,
      targetPositionComponents,
      colorComponents,
    } = dataComponents;

    for (let i = 0; i < toAdd; i++) {
      const sourceIndex = sourceOffset + i;
      const targetIndex = this.count + i;

      // Source indices (CrossSectionPointData format)
      const sourcePosIdx = sourceIndex * sourcePositionComponents;
      const sourceColorIdx = sourceIndex * colorComponents;

      // Target indices (Three.js format)
      const targetPosIdx = targetIndex * targetPositionComponents;
      const targetColorIdx = targetIndex * colorComponents;

      // Copy position: [mileage, 0, elevation]
      targetPositions[targetPosIdx] = data.positions[sourcePosIdx]; // mileage (x)
      targetPositions[targetPosIdx + 1] = 0; // y = 0
      targetPositions[targetPosIdx + 2] = data.positions[sourcePosIdx + 3]; // elevation (z)

      // Copy color: [r, g, b]
      targetColors[targetColorIdx] = data.colors[sourceColorIdx];
      targetColors[targetColorIdx + 1] = data.colors[sourceColorIdx + 1];
      targetColors[targetColorIdx + 2] = data.colors[sourceColorIdx + 2];
    }

    this.count += toAdd;
    this.updateGeometry();

    return toAdd;
  }

  /**
   * Get point data at a specific local index
   */
  getPointAt(localIndex: number): PointData | null {
    if (localIndex < 0 || localIndex >= this.count) {
      return null;
    }

    const positions = this.positionAttribute.array as Float32Array;
    const colors = this.colorAttribute.array as Uint8Array;
    const { targetPositionComponents, colorComponents } = dataComponents;
    const positionIndex = localIndex * targetPositionComponents;
    const colorIndex = localIndex * colorComponents;

    return {
      mileage: positions[positionIndex],
      z: positions[positionIndex + 3],
      r: colors[colorIndex],
      g: colors[colorIndex + 1],
      b: colors[colorIndex + 2],
    };
  }

  /**
   * Dispose of GPU resources.
   */
  dispose(): void {
    this.geometry.dispose();
  }
}

// PointBatchRenderer Class.

/**
 * Manages multiple batches of points for efficient rendering.
 */
export class PointBatchRenderer {
  private readonly scene: THREE.Scene;
  private readonly sharedMaterial: THREE.PointsMaterial;
  private readonly batchSize: number;
  private readonly batches: PointBatch[] = [];
  private readonly bounds: THREE.Box3 = new THREE.Box3();

  private currentBatch: PointBatch | null = null;
  private totalPoints: number = 0;

  constructor(scene: THREE.Scene, options: PointBatchRendererOptions = {}) {
    this.scene = scene;
    this.batchSize = options.batchSize ?? DEFAULT_BATCH_SIZE;
    this.sharedMaterial = this.createMaterial(options);
  }

  /**
   * Create material from options.
   */
  private createMaterial(
    options: PointBatchRendererOptions,
  ): THREE.PointsMaterial {
    if (options.material) {
      return options.material;
    }

    return new THREE.PointsMaterial({
      size: options.size ?? 5,
      vertexColors: true,
      sizeAttenuation: false,
      map: options.map ?? null,
      transparent: options.transparent ?? false,
      opacity: options.opacity ?? 1.0,
      depthTest: options.depthTest ?? false,
      depthWrite: options.depthWrite ?? false,
      color: options.color ?? 0xffffff,
    });
  }

  /**
   * Create and register a new batch.
   */
  private createNewBatch(): void {
    this.currentBatch = new PointBatch(this.batchSize, this.sharedMaterial);
    this.batches.push(this.currentBatch);
    this.scene.add(this.currentBatch.points);
  }

  /**
   * Load point data directly from TypedArray format.
   * Optimized version - no intermediate object array conversion.
   * @param data Cross-section point data in TypedArray format
   */
  loadData(data: CrossSectionPointData): void {
    const pointsToAdd = data.count;
    let sourceOffset = 0;

    while (sourceOffset < pointsToAdd) {
      if (!this.currentBatch || this.currentBatch.isFull()) {
        this.createNewBatch();
      }

      if (!this.currentBatch) {
        break;
      }

      const added = this.currentBatch.addPoints(
        data,
        sourceOffset,
        pointsToAdd - sourceOffset,
      );

      this.updateBoundsForPoints(data.positions, sourceOffset, added);
      sourceOffset += added;
      this.totalPoints += added;
    }
  }

  /**
   * Update bounding box for a range of points from TypedArray.
   * @param positions Source positions TypedArray
   * @param startOffset Starting index in source array
   * @param count Number of points to process
   */
  private updateBoundsForPoints(
    positions: Float32Array,
    startOffset: number,
    count: number,
  ): void {
    const { sourcePositionComponents } = dataComponents;
    for (let i = 0; i < count; i++) {
      const posIdx = (startOffset + i) * sourcePositionComponents;
      this.bounds.expandByPoint(
        new THREE.Vector3(positions[posIdx], 0, positions[posIdx + 3]),
      );
    }
  }

  /**
   * Get bounding box of all points.
   */
  getBounds(): THREE.Box3 {
    return this.bounds.clone();
  }

  /**
   * Update material properties for all batches.
   */
  setMaterialProperties(options: Partial<PointBatchRendererOptions>): void {
    if (!isUndefined(options.size)) {
      this.sharedMaterial.size = options.size;
    }

    if (!isUndefined(options.opacity)) {
      this.sharedMaterial.opacity = options.opacity;
      this.sharedMaterial.transparent =
        options.transparent ?? options.opacity < 1.0;
    }

    if (!isUndefined(options.transparent)) {
      this.sharedMaterial.transparent = options.transparent;
    }

    if (!isUndefined(options.map)) {
      this.sharedMaterial.map?.dispose();
      this.sharedMaterial.map = options.map;
    }

    if (!isUndefined(options.depthTest)) {
      this.sharedMaterial.depthTest = options.depthTest;
    }

    if (!isUndefined(options.depthWrite)) {
      this.sharedMaterial.depthWrite = options.depthWrite;
    }

    if (!isUndefined(options.color)) {
      this.sharedMaterial.color.setHex(options.color);
    }

    this.sharedMaterial.needsUpdate = true;
  }

  /**
   * Get point data by global index.
   */
  getPointData(globalIndex: number): PointData | null {
    if (globalIndex < 0 || globalIndex >= this.totalPoints) {
      return null;
    }

    let currentOffset = 0;

    for (const batch of this.batches) {
      const nextOffset = currentOffset + batch.count;

      if (globalIndex < nextOffset) {
        const localIndex = globalIndex - currentOffset;
        return batch.getPointAt(localIndex);
      }

      currentOffset = nextOffset;
    }

    return null;
  }

  /**
   * Get all point meshes useful for raycasting.
   */
  getPointMeshes(): THREE.Points[] {
    return this.batches.map((batch) => batch.points);
  }

  /**
   * Get renderer statistics.
   */
  getStats(): RendererStats {
    return {
      totalPoints: this.totalPoints,
      batchCount: this.batches.length,
      batchSize: this.batchSize,
    };
  }

  /**
   * Clear all batches from scene.
   */
  clear(): void {
    for (const batch of this.batches) {
      this.scene.remove(batch.points);
      batch.dispose();
    }

    this.batches.length = 0;
    this.currentBatch = null;
    this.totalPoints = 0;
    this.bounds.makeEmpty();
  }

  /**
   * Dispose of all resources.
   */
  dispose(): void {
    this.clear();
    this.sharedMaterial.dispose();
  }
}
